/**
 * Aba "Acompanhamento Silencioso" (Out-of-Hours) da Tela A2.
 *
 * Expõe os campos REAIS do `tenant-config` (R15): `businessHours` (janelas de
 * horário comercial por dia) + `outOfHoursMessage` (auto-resposta fora do
 * horário). O motor já existe (`OutOfHoursService` + listener); esta aba só
 * dá ao admin a UI de configuração. Escrita gated em `tenant-config-manage`.
 *
 * Modelo: 1 janela por dia da semana (o backend aceita até 21; a UI cobre o
 * caso comum de um intervalo contínuo por dia — dias sem janela = fechados).
 *
 * ★ Achado + corrigido (Admin Hub T8 fix round 1, task-8-report.md): o item
 * de catálogo `acompanhamento-silencioso` (`hub/catalog.ts`) tinha herdado —
 * quase verbatim — a description da capability de PLATAFORMA `silent-monitoring`
 * (`packages/shared/src/capabilities/capabilities.spec.yaml:544`, ADR-094:
 * permissão per-user de supervisor observar conversa sem sinalizar presença).
 * Um conceito TOTALMENTE DIFERENTE do que este componente faz (horário
 * comercial + auto-resposta), coincidência de nome («silencioso») entre um
 * título de aba pré-existente e uma capability real não relacionada — a
 * mesma classe de erro registrada em CLAUDE.md v3.801 (uma palavra, dois
 * significados). `catalog.ts` já foi corrigido (`label`/`efeito` agora
 * descrevem ISTO; pinado em `catalog.spec.ts`) — por isso o header local
 * abaixo pôde sair (ver próximo parágrafo). `id`/`dominio` do catálogo NÃO
 * mudaram — decisão do coordenador, vira item para o TL.
 *
 * Admin Hub T8 — os botões locais Descartar/Salvar saíram; a seção registra na
 * `StickySaveBar` do domínio via `useSectionSave` (mesmo movimento do `GeneralTab`,
 * T7). `changedLabels` distingue os 2 campos reais (`businessHours` acumula
 * qualquer edição de dia/horário sob um único label — o backend não diferencia
 * dia-a-dia no payload). Fix round 1: o `<header>` próprio SAIU — com o
 * catálogo corrigido (parágrafo acima), `DomainSection` já supre título +
 * descrição corretos; manter os dois seria o título duplicado que a T7 já
 * tinha evitado em `GeneralTab`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useSectionSave } from '../hub/save-bar-context';

import { SILENT_TAB_FIELD_LABELS } from './config-tenant-labels';
import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type { BusinessHourRange, SerializedTenantConfig, UpdateTenantConfigRequest } from './types';

const DAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

interface DayState {
  enabled: boolean;
  openHour: number;
  closeHour: number;
}
interface FormState {
  days: DayState[]; // index = dayOfWeek 0-6
  outOfHoursMessage: string;
}

function toForm(c: SerializedTenantConfig): FormState {
  const days: DayState[] = DAY_LABELS.map((_, dow) => {
    const range = c.businessHours.find((r) => r.dayOfWeek === dow);
    return range !== undefined
      ? { enabled: true, openHour: range.openHour, closeHour: range.closeHour }
      : { enabled: false, openHour: 8, closeHour: 18 };
  });
  return { days, outOfHoursMessage: c.outOfHoursMessage ?? '' };
}

function buildBusinessHours(days: DayState[]): BusinessHourRange[] {
  return days
    .map((d, dayOfWeek) => ({ d, dayOfWeek }))
    .filter(({ d }) => d.enabled)
    .map(({ d, dayOfWeek }) => ({ dayOfWeek, openHour: d.openHour, closeHour: d.closeHour }));
}

export function SilentTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  // Há janela com closeHour <= openHour? (bloqueia o save — backend rejeitaria)
  const hasInvalidRange = useMemo(
    () => (form?.days ?? []).some((d) => d.enabled && d.closeHour <= d.openHour),
    [form],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  // Payload de escrita a partir de UM form — usado nos dois sentidos:
  // `buildRequest(form)` (o que enviar) e `buildRequest(serverForm)` (valores
  // antigos, para o `undo`, pela MESMA mutation).
  const buildRequest = (f: FormState): UpdateTenantConfigRequest => ({
    businessHours: buildBusinessHours(f.days),
    outOfHoursMessage: f.outOfHoursMessage.trim() === '' ? null : f.outOfHoursMessage,
  });

  // Só os 2 campos reais que MUDARAM — `businessHours` cobre qualquer edição
  // de dia/horário sob um único label (o payload não diferencia dia-a-dia).
  const changedLabels = useMemo(() => {
    if (form === null || serverForm === null) return [];
    const labels: string[] = [];
    if (JSON.stringify(form.days) !== JSON.stringify(serverForm.days)) {
      labels.push(SILENT_TAB_FIELD_LABELS.businessHours);
    }
    if (form.outOfHoursMessage !== serverForm.outOfHoursMessage) {
      labels.push(SILENT_TAB_FIELD_LABELS.outOfHoursMessage);
    }
    return labels;
  }, [form, serverForm]);

  const previousValues = useRef<UpdateTenantConfigRequest | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  // Precisa ser chamado incondicionalmente em todo render (regra dos hooks) —
  // antes dos returns condicionais de loading/erro abaixo.
  useSectionSave(
    isDirty && canManage && !hasInvalidRange && form !== null && serverForm !== null
      ? {
          sectionId: 'acompanhamento-silencioso',
          changedLabels,
          save: async () => {
            previousValues.current = buildRequest(serverForm);
            await update.mutateAsync(buildRequest(form));
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
            }
          },
        }
      : null,
  );

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;

  const setDay = (dow: number, patch: Partial<DayState>): void => {
    setForm((f) =>
      f === null ? f : { ...f, days: f.days.map((d, i) => (i === dow ? { ...d, ...patch } : d)) },
    );
  };

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (fix round 1, T8) — DomainSection já supre título
          (item.label, agora "Horário de atendimento") + descrição
          (item.efeito, agora correta) quando esta aba é composta no hub. Um
          detalhe do header antigo NÃO foi dobrado no catálogo: "a conversa
          fica registrada para atendimento prioritário" — é um efeito
          colateral de fila (mecânica de outro subsistema), não o que ESTA
          seção configura; ver task-8-report.md (fix round 1). */}
      <div className="space-y-6">
        {/* Horário comercial por dia */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <h3 className="text-sm font-medium text-text-primary">Horário de atendimento</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Dias desligados são considerados fora do horário o dia todo.
          </p>
          <div className="space-y-2">
            {form.days.map((day, dow) => {
              const dayLabel = DAY_LABELS[dow] ?? `Dia ${dow.toString()}`;
              return (
                <div key={dayLabel} className="flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={day.enabled}
                    aria-label={dayLabel}
                    disabled={fieldsDisabled}
                    onClick={() => {
                      setDay(dow, { enabled: !day.enabled });
                    }}
                    className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                      day.enabled ? 'bg-accent' : 'bg-border-subtle'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                        day.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <span className="w-20 flex-shrink-0 text-sm text-text-primary">{dayLabel}</span>
                  {day.enabled ? (
                    <div className="flex items-center gap-2 text-sm">
                      <HourSelect
                        label={`Abre ${dayLabel}`}
                        min={0}
                        max={23}
                        value={day.openHour}
                        disabled={fieldsDisabled}
                        onChange={(v) => {
                          setDay(dow, { openHour: v });
                        }}
                      />
                      <span className="text-text-secondary">às</span>
                      <HourSelect
                        label={`Fecha ${dayLabel}`}
                        min={1}
                        max={24}
                        value={day.closeHour}
                        disabled={fieldsDisabled}
                        onChange={(v) => {
                          setDay(dow, { closeHour: v });
                        }}
                      />
                      {day.closeHour <= day.openHour && (
                        <span className="text-xs text-red-600">fim deve ser após o início</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-text-secondary">Fechado</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mensagem fora do horário */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <label htmlFor="cfg-ooh-message" className="text-sm font-medium text-text-primary">
            Mensagem fora do horário
          </label>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Placeholders: <code className="font-mono">{'{tenantName}'}</code> e{' '}
            <code className="font-mono">{'{nextOpenAt}'}</code>. Vazio = sem auto-resposta.
          </p>
          <textarea
            id="cfg-ooh-message"
            aria-label="Mensagem fora do horário"
            rows={3}
            maxLength={2000}
            disabled={fieldsDisabled}
            value={form.outOfHoursMessage}
            onChange={(e) => {
              setForm((f) => (f === null ? f : { ...f, outOfHoursMessage: e.target.value }));
            }}
            className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
          />
        </div>
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T8); sucesso/erro do save viram toast da barra, não mensagem local.
          Uma janela inválida (fim <= início) apenas retira a seção da barra
          (útil: o backend rejeitaria; o aviso por dia já sinaliza o problema
          acima). */}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}

function HourSelect({
  label,
  min,
  max,
  value,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly value: number;
  readonly disabled: boolean;
  readonly onChange: (v: number) => void;
}): JSX.Element {
  const options: number[] = [];
  for (let h = min; h <= max; h++) options.push(h);
  return (
    <select
      aria-label={label}
      disabled={disabled}
      value={value}
      onChange={(e) => {
        onChange(Number(e.target.value));
      }}
      className="rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
    >
      {options.map((h) => (
        <option key={h} value={h}>
          {h.toString().padStart(2, '0')}h
        </option>
      ))}
    </select>
  );
}
